import * as React from "react";
import { usePeekInContext } from "../PeekInContext";
import type { TypePeekInFooterProps } from "../PeekInTypes";

/**
 * Clones a footer slot and chains PeekIn's close onto the child's own onClick.
 *
 * ModalFooter reaches for Radix's Dialog.Close here, but PeekIn must work on
 * both of its renderings — a Radix-backed Modal on desktop and a Base UI Drawer
 * on mobile — so it closes through PeekIn's own context instead. That also keeps
 * the footer independent of whichever dialog primitive Modal composes.
 *
 * A slot that calls preventDefault() suppresses the close, which is how a
 * consumer keeps the overlay open on a failed validation.
 */
const CloseOnClick = ({ children }: { children: React.ReactNode }) => {
  const { onClose } = usePeekInContext();

  if (!React.isValidElement(children)) {
    return <>{children}</>;
  }

  const child = children as React.ReactElement<any>;

  return React.cloneElement(child, {
    onClick: (e: React.MouseEvent) => {
      child.props.onClick?.(e);
      if (!e.defaultPrevented) onClose();
    },
  });
};

/**
 * Footer for PeekIn's main column, holding the overlay's primary actions.
 *
 * Slot behavior matches ModalFooter exactly:
 * - `cancelButton` always closes
 * - `primaryButton` closes unless `closeOnPrimaryAction` is false
 * - `leftAction` never closes — the consumer owns it
 *
 * @example
 * <PeekInFooter
 *   leftAction={<Button appearance="destructive">Delete</Button>}
 *   cancelButton={<Button>Cancel</Button>}
 *   primaryButton={<Button appearance="primary">Save</Button>}
 * />
 */
export const PeekInFooter: React.FC<TypePeekInFooterProps> = (props) => {
  const { primaryButton, cancelButton, leftAction } = props;
  const closeOnPrimaryAction = props.closeOnPrimaryAction ?? true;

  // Defensive — the prop union already requires at least one action.
  if (!primaryButton && !cancelButton && !leftAction) {
    return null;
  }

  return (
    <div
      className="seeds-peek-in-footer"
      // The mobile Drawer's bottom sheet deliberately bleeds 3rem below the
      // viewport for elastic overscroll, so an unlifted footer lands inside the
      // bleed and clips below the fold. --bleed is only set at runtime by the
      // mobile Drawer and resolves to 0px on desktop, so this is safe on both
      // paths. Same compensation as PanelFooter; it stays inline rather than in
      // peek-in.css because only this one declaration is runtime-driven.
      style={{ marginBottom: "var(--bleed, 0px)" }}
      data-slot="peek-in-footer"
      data-qa-peek-in-footer
    >
      {/* Far-left action (e.g. Delete) — never auto-closes */}
      {leftAction ?? null}

      {/* Right-aligned group. Its margin-left: auto is what pushes it right
          past a leftAction. */}
      <div className="seeds-peek-in-footer-actions">
        {cancelButton && <CloseOnClick>{cancelButton}</CloseOnClick>}
        {primaryButton && closeOnPrimaryAction ? (
          <CloseOnClick>{primaryButton}</CloseOnClick>
        ) : (
          primaryButton ?? null
        )}
      </div>
    </div>
  );
};

PeekInFooter.displayName = "PeekInFooter";
